import { ChangeDetectionStrategy, Component, Inject, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SelectOnFocusDirective } from 'src/app/shared/directives/select-on-focus.directive';
import { TwoDecimalsDirective } from 'src/app/shared/directives/two-decimals.directive';

/** The priced security a calculation is being run against. */
export interface ShareCalculatorData {
  ticker: string;
  description: string | null;
  /** Orion's price. The caller must not open this dialog without one — see `price > 0` there. */
  price: number;
  priceAsOf: Date | null;

  /**
   * Which side this is, which changes the words and nothing else. Defaults to buying.
   *
   * ⚠️ The ARITHMETIC is deliberately identical. "How many shares is $X" has one answer, and
   * giving each side its own copy is how the two drift into rounding differently — a difference
   * nobody would notice until a distribution came out a share short of a buy for the same money.
   */
  mode?: 'buy' | 'sell';

  /**
   * The most shares this row can produce — the position's own share count when selling.
   *
   * ⚠️ Null when unbounded, which is the buy case: what the account can afford is
   * `buysExceedInvestable`'s question, asked against every row at once. A sell has a real,
   * per-row ceiling — you cannot sell more than you hold — so the answer is capped rather than
   * left to be rejected later by the share input's own clamp.
   */
  maxShares?: number | null;
}

/**
 * Work out a share count from a dollar amount, for a security that has a price.
 *
 * The gap it fills: the advisor thinks in dollars ("$5,000 of AAPL") and the buy row wants
 * shares, so they were dividing by the price on a phone calculator and typing the result back
 * in. The estimate the row then shows is derived from that share count, so an arithmetic slip
 * here is invisible afterwards.
 *
 * ⚠️ Returns WHOLE shares, rounded DOWN, and the two halves of that matter separately.
 *
 * Whole, because the trade team executes on the share count — handing them 21.087 shares
 * invites a partial-share order nobody asked for.
 *
 * ⚠️ This used to add "the share input still accepts a fraction if the advisor types one; this
 * just does not manufacture one". No longer true on a DISTRIBUTION: the typed figure is floored
 * there too (see the `sharesToSell` clamp in `buildPositionsForm`), so the calculator and the box
 * beside it now agree. The exception is SELL ALL, which sells a fractional lot whole.
 *
 * Down, because rounding up spends money the advisor did not agree to. The overshoot would
 * land in the investable balance and be caught two steps later by `buysExceedInvestable`,
 * which is a poor way to learn that a calculator added $237 to the request. Rounding down can
 * leave the result visibly short of the target on a high-priced security, which is why the
 * resulting cost is on screen beside the share count rather than left to be discovered.
 *
 * ⚠️ Deliberately knows nothing about the investable balance. Whether the account can afford
 * the buy is `buysExceedInvestable`'s question, asked against every row at once; answering a
 * narrower version of it here would put two verdicts on screen that could disagree.
 */
@Component({
  selector: 'app-rav2-share-calculator-modal',
  standalone: true,
  imports: [CommonModule, SelectOnFocusDirective, TwoDecimalsDirective],
  templateUrl: './share-calculator-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShareCalculatorModalComponent {
  /** What the advisor wants to spend. Null while the box is empty. */
  readonly amount = signal<number | null>(null);

  /** Selling rather than buying. Switches the copy; the arithmetic is the same either way. */
  readonly isSell = computed(() => this.data.mode === 'sell');

  /**
   * The raw answer, before the position's own ceiling.
   *
   * Kept separate so {@link isCapped} can tell "you asked for more than this position holds"
   * from "that is the answer" — the two want different words.
   */
  private readonly uncappedShares = computed(() => {
    const amount = this.amount();
    if (amount == null || !Number.isFinite(amount) || amount <= 0) return 0;
    return Math.floor(amount / this.data.price);
  });

  readonly shares = computed(() => {
    const max = this.data.maxShares;
    const uncapped = this.uncappedShares();
    return max == null ? uncapped : Math.min(uncapped, Math.floor(max));
  });

  /**
   * Whether the amount asked for more than the position holds.
   *
   * ⚠️ Worth saying rather than silently handing back a smaller number: the advisor typed a
   * figure and is getting fewer shares than it buys, and without this the only clue is that the
   * proceeds below do not match what they asked for.
   */
  readonly isCapped = computed(() => this.shares() < this.uncappedShares());

  /**
   * What those shares actually cost, or raise.
   *
   * ⚠️ An ESTIMATE on both sides, and more obviously so when selling: the price is Orion's last
   * mark, and the trade executes later at whatever the market gives. The figure is here to show
   * the rounding, not to promise the proceeds.
   */
  readonly estimatedCost = computed(() => this.shares() * this.data.price);

  /** What the rounding gave up. Shown only when it is worth a cent or more. */
  readonly shortfall = computed(() => {
    const amount = this.amount() ?? 0;
    return this.shares() > 0 ? amount - this.estimatedCost() : 0;
  });

  /**
   * Whether there is a usable answer.
   *
   * A whole share is the smallest thing this can produce, so an amount under one share's price
   * has no answer rather than a zero one — applying 0 shares would silently blank a figure the
   * advisor may already have typed.
   */
  readonly canApply = computed(() => this.shares() > 0);

  constructor(
    readonly dialogRef: MatDialogRef<ShareCalculatorModalComponent, number | null>,
    @Inject(MAT_DIALOG_DATA) readonly data: ShareCalculatorData,
  ) {}

  /**
   * ⚠️ Bound to `input`, not `change`, unlike every other money box in this wizard.
   *
   * Those commit on blur because a half-typed figure would drive the running calculation and
   * swing the totals through 5, 50 and 500 on the way to 500. Nothing here reaches the request:
   * it is a scratch pad, and watching the share count settle as you type is the entire point of
   * the control.
   */
  onAmount(event: Event): void {
    const raw = (event.target as HTMLInputElement).value.trim();
    const parsed = Number.parseFloat(raw);
    this.amount.set(raw === '' || !Number.isFinite(parsed) ? null : parsed);
  }

  apply(): void {
    if (this.canApply()) this.dialogRef.close(this.shares());
  }
}
